import { oktaAuth } from './okta-auth';

// Same-origin: requests are proxied to the adapter by the bundled nginx
// (/api/admin/* -> ADMIN_UI_GATEWAY_INTERNAL_URL). The browser never sees
// the adapter URL. config.gatewayUrl is still used for user-facing
// pre-consent share links via getPublicConfig() (sourced server-side from
// the adapter's GATEWAY_BASE_URL).
const API_BASE_URL = '';

export interface Agent {
  agent_id: string;
  agent_name?: string;
  client_id: string;
  client_secret?: string;
  private_key?: string;

  enabled: boolean;
  dcr_selectable?: boolean;
  dcr_redirect_uris?: string[];
  principal_id?: string;
  cimd_client_id_url?: string;
  okta_ai_agent_id?: string;
  registration_source?: string;
  selected_delegation_app_id?: string | null;
  effective_client_id?: string | null;
  metadata?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

// Master resource — one row per logical MCP target. Connection-level
// state (auth_method, scopes, metadata, …) lives on agent_connections
// after migration 004 and is no longer returned at the resource level.
// Pre-split fields are kept here as deprecated shapes so existing
// components don't fail to compile during the UI rollout — they are
// undefined on every response from the backend post-split.
export interface Resource {
  id?: number;
  name: string;
  type?: string;
  url?: string;
  mcp_url?: string;
  protocol?: string;
  description: string;
  paths?: string[];
  config?: Record<string, any>;
  tags?: string[];
  timeout_seconds?: number | null;
  enabled?: boolean;

  // Linked-connection summary (admin_get embeds; admin_list provides
  // count-only).
  linked_connection_count?: number;
  linked_connections?: LinkedConnection[];

  /** @deprecated moved to agent_connections after migration 004. */
  agent_id?: string;
  /** @deprecated moved to agent_connections after migration 004. */
  connection_id?: string;
  /** @deprecated moved to agent_connections after migration 004. */
  connection_type?: string;
  /** @deprecated moved to agent_connections after migration 004. */
  connection_resource_id?: string | null;
  /** @deprecated moved to agent_connections after migration 004. */
  scopes?: string[];
  /** @deprecated moved to agent_connections after migration 004. */
  scope_condition?: string;
  /** @deprecated moved to agent_connections after migration 004. */
  auth_method?: string | null;
  /** @deprecated moved to agent_connections after migration 004. */
  metadata?: Record<string, any>;
  /** @deprecated moved to agent_connections after migration 004. */
  auth_config?: Record<string, any>;

  created_at?: string;
  updated_at?: string;
}

/** Connection summary embedded in admin_get(name) responses. */
export interface LinkedConnection {
  agent_id: string;
  agent_name: string;
  connection_id: string;
  connection_type: string;
  auth_method: string | null;
  auth_config: Record<string, any>;
  scopes: string[];
  scope_condition: string;
  enabled: boolean;
}

/** Body accepted by POST /api/admin/resources. Post-split, masters can
 * be created standalone (no Okta sync required). */
export interface CreateResourceInput {
  name: string;
  type?: string;
  url?: string;
  description?: string;
  paths?: string[];
  config?: Record<string, any>;
  tags?: string[];
  timeout_seconds?: number;
  enabled?: boolean;
}

/** Result of POST /api/admin/resources/{name}/merge. */
export interface MergeResult {
  target: string;
  deleted_resource: string;
  moved_connections: number;
  moved_tools: number;
  dropped_tools: number;
}

/** One group from GET /api/admin/resources/merge-candidates. */
export interface MergeCandidateGroup {
  connection_resource_id: string;
  candidates: Array<{
    resource_id: number;
    resource_name: string;
    url: string;
    enabled: boolean;
    agents: Array<{ agent_id: string; connection_id: string }>;
  }>;
}

// Backward-compat alias
export type ResourceMapEntry = Resource;

export interface SyncResult {
  added: string[];
  removed: string[];
  modified: string[];
  unresolved: string[];
  total: number;
  synced_at: string;
}

export interface ConnectionStatus {
  syncer: {
    last_sync_at: string | null;
    status: string;
    is_stale: boolean;
    resources_count: number;
    unresolved_count: number;
  };
  last_persisted_state: {
    last_sync_at: string | null;
    status: string;
    resources_count: number;
    unresolved_count: number;
    sync_duration_ms: number;
  } | null;
}

export interface AuditLogEntry {
  id: number;
  changed_at: string;
  action: string;
  entity_type?: string;
  entity_id?: string;
  changed_by?: string;
  ip_address?: string;
  user_agent?: string;
  changes?: Record<string, any>;
  details?: Record<string, any>;
}

export interface EventLogEntry {
  timestamp: string;
  event_type: string;
  severity: string;
  agent_id: string;
  outcome: string;
  correlation_id: string;
  message: string;
}

export interface EventLogResponse {
  provider: string;
  events: EventLogEntry[];
  query_time_ms?: number;
  message?: string;
}

export interface OktaAgent {
  okta_agent_id: string;
  name: string;
  description: string;
  status: string;
  app_id: string | null;
  linked_app_client_id: string | null;
  connections_count: number;
  already_imported: boolean;
  oauth_client_id?: string;
  sign_on_provider_app_instance_id?: string;
  selected_delegation_app_id?: string | null;
}

export interface DelegationAppCandidate {
  app_instance_id: string;
  client_id: string | null;
  label: string | null;
  source: string;
}

export interface OktaAgentDetail {
  agent: Record<string, any>;
  connections: Array<{
    connection: Record<string, any>;
    matched_resource: string | null;
  }>;
  linked_app: {
    app_id: string | null;
    client_id: string | null;
    label: string | null;
    sign_on_provider_app_instance_id?: string | null;
  } | null;
}

export interface ImportResult {
  success: boolean;
  agent_name: string;
  okta_agent_id: string;
  message: string;
  resources_linked: string[];
  warnings: string[];
  selection_required?: boolean;
  candidates?: DelegationAppCandidate[];
}

export interface SyncAllResult {
  agents: {
    results: ImportResult[];
    synced: number;
    failed: number;
    unchanged: number;
  };
  resources: {
    resources_updated: string[];
    unchanged: string[];
  };
}

// A Connection's resource row (0 or 1 per agent+connection pair)
export interface ConnectionResource {
  id?: number;
  agent_id?: string;
  connection_id?: string;
  name: string;
  type?: string;
  url?: string;
  description?: string;
  connection_type?: string;
  connection_resource_id?: string | null;
  scopes?: string[];
  scope_condition?: string;
  auth_method?: string | null;
  metadata?: Record<string, any>;
  enabled?: boolean;
}

// Connection from Okta with its single resource row (or null)
export interface AgentConnection {
  connection_id: string;
  connection_type: 'IDENTITY_ASSERTION_CUSTOM_AS' | 'STS_VAULT_SECRET' | 'STS_SERVICE_ACCOUNT' | 'STS_ACCESS_TOKEN' | 'SECRET' | 'SERVICE_ACCOUNT';
  display_name: string;
  resource_id: string;
  auth_server_name: string;
  resource_indicator: string;
  scopes: string[];
  scope_condition: 'INCLUDE_ONLY' | 'DISALLOW' | 'ALLOW_ALL' | null;
  status: string;
  resource: ConnectionResource | null;
}

// Full agent detail with connections
export interface AgentDetail {
  agent: Agent;
  okta_agent: Record<string, any> | null;
  connections: AgentConnection[];
  linked_app: {
    app_id: string | null;
    client_id: string | null;
    label: string | null;
  } | null;
  stats: {
    total_connections: number;
    fully_linked_count: number;
    partially_linked_count: number;
    unlinked_count: number;
    inactive_count: number;
    total_resources: number;
    total_tools: number;
  };
}

// Tool catalog (read from the persisted tool inventory)
export type ToolSensitivity = 'low' | 'medium' | 'high' | 'critical' | null;
export type DiscoveryVia = 'active' | 'passive_unified' | 'passive_path';
export type DiscoveryStatus = 'never' | 'success' | 'failure' | 'skipped';
export type DiscoveryMode = 'active' | 'passive' | 'unknown';

export interface AgentTool {
  name: string;
  description?: string;
  inputSchema?: Record<string, any>;
  annotations?: Record<string, any>;
  sensitivity?: ToolSensitivity;
  mutating?: boolean | null;
  category?: string | null;
  tags?: string[];
  enabled?: boolean;
  hidden_reason?: string | null;
}

export interface AgentToolsResourceGroup {
  resource_name: string;
  tool_count: number;
  tools: AgentTool[];
}

export interface AgentToolsResponse {
  agent_id: string;
  cached: boolean;
  total_tools: number;
  resources: AgentToolsResourceGroup[];
  // Present on paginated responses; pagination.total mirrors total_tools.
  pagination?: Pagination;
}

// Persisted tool inventory (resource-scoped)
export interface ResourceTool {
  resource_id: number;
  resource_name?: string;
  tool_name: string;
  description: string | null;
  input_schema: Record<string, any>;
  annotations: Record<string, any>;
  sensitivity: ToolSensitivity;
  mutating: boolean | null;
  category: string | null;
  tags: string[];
  discovered_at: string;
  discovered_via: DiscoveryVia;
  last_seen_at: string;
  consecutive_misses: number;
  enabled: boolean;
  hidden_reason: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ResourceToolDiscoveryState {
  resource_id: number;
  resource_name?: string;
  auth_method?: string | null;
  last_run_at: string | null;
  last_status: DiscoveryStatus | null;
  last_error: string | null;
  last_tool_count: number | null;
  last_duration_ms: number | null;
  next_run_at: string | null;
  discovery_mode: DiscoveryMode | null;
}

// Tool annotation types (P10)
export type AnnotationSource = 'manual' | 'llm_runtime' | 'llm_discovery' | 'mcp_server';
export type AnnotationConfidence = 'high' | 'medium' | 'low';

export interface InferenceEnvelope {
  source: AnnotationSource;
  confidence: AnnotationConfidence;
  inferred_at: string;
  model?: string | null;
  provider?: string | null;
  input_hash?: string | null;
  reasoning?: string | null;
}

export interface ToolAnnotations {
  mutating?: boolean | null;
  destructive?: boolean | null;
  data_access_level?: 'none' | 'read' | 'write' | null;
  categories: string[];
  _inference?: InferenceEnvelope;
  [key: string]: unknown;
}

export interface SetAnnotationsInput {
  mutating?: boolean | null;
  destructive?: boolean | null;
  data_access_level?: 'none' | 'read' | 'write' | null;
  categories?: string[];
}

export const VALID_ANNOTATION_CATEGORIES = [
  'pii', 'financial', 'credentials', 'system_config', 'user_management', 'audit_log',
] as const;

// Pagination envelope returned by all paginated tool inventory endpoints.
export interface Pagination {
  limit: number;
  offset: number;
  total: number;
  has_more: boolean;
}

export interface ListAllToolsResponse {
  tools: ResourceTool[];
  total: number;
  unclassified_count: number;
  disabled_count: number;
  pagination: Pagination;
}

export interface ListResourceToolsResponse {
  resource_name: string;
  tools: ResourceTool[];
  discovery_state: ResourceToolDiscoveryState | null;
  pagination: Pagination;
}

export interface DiscoveryStateListResponse {
  resources: ResourceToolDiscoveryState[];
  pagination: Pagination;
}

// One row per resource — drives the resources-list page tools / unclassified
// columns in lieu of a full listAllTools() round trip.
export interface ResourceToolCounts {
  resource_name: string;
  total: number;
  unclassified: number;
  disabled: number;
}

export interface ListResourceToolCountsResponse {
  resources: ResourceToolCounts[];
}

export interface ToolClassificationInput {
  sensitivity?: 'low' | 'medium' | 'high' | 'critical' | null;
  mutating?: boolean | null;
  category?: string | null;
  tags?: string[];
}

export interface BulkClassifyEntry extends ToolClassificationInput {
  tool_name: string;
}

export interface BulkClassifyResult {
  updated: number;
  errors: Array<{ tool_name: string | null; error: string }>;
}

export interface DiscoveryTriggerResult {
  status: 'started' | 'skipped' | 'completed' | 'failed' | string;
  summary?: Record<string, any>;
  reason?: string;
  [key: string]: any;
}

// Response from GET /api/admin/agents/{agentId}/connections
export interface AgentConnectionsResponse {
  agent: Agent;
  connections: AgentConnection[];
  stats: {
    total_connections: number;
    fully_linked_count: number;
    unlinked_count: number;
    inactive_count: number;
    total_resources: number;
    total_tools: number;
  };
}

export interface ApiError {
  error: string;
  message: string;
  required_scopes?: string[];
}

export interface PublicConfig {
  gateway_base_url: string;
  mcp_protocol_version: string;
  // Every backend setting tagged Field(json_schema_extra={"ui_flag": True}),
  // camelCased. No enum to keep in sync — flags are referenced by name
  // directly (e.g. featureFlags.cedarEnabled).
  feature_flags: Record<string, boolean>;
  agent_routing_enabled: boolean;
}

export interface CredentialStatus {
  has_client_secret: boolean;
  has_private_key: boolean;
  registered_keys: { kid: string; alg: string }[];
  okta_agent_id: string | null;
  token_endpoint_auth_method?: string | null;
  app_key_management_mode?: string | null;
  key_target?: 'app' | 'agent';
  agent_key_delivery_mode?: string | null;
}

export interface FetchSecretResult {
  status: string;
  source: string;
  app_id: string;
}

export interface GenerateKeyPairResult {
  status: string;
  kid: string;
  agent_id: string;
  delivery_mode?: string;
}

export interface ProxySecretStatus {
  enabled: boolean;
  prefix: string | null;
  created_at: string | null;
  rotated_at: string | null;
  revealed_at: string | null;
  last_used_at: string | null;
}

export interface ProxySecretReveal extends ProxySecretStatus {
  secret: string;
  warning: string;
}

export interface ForceLogoutSummary {
  user_sub: string;
  correlation_id: string;
  tokens_revoked: number;
  cache_swept: boolean;
  sessions_terminated: number;
  sessions_failed: number;
  backends: Record<string, boolean>;
  // Added in v0.15.x: relay-tracked CIMD/DCR tokens purged for this user.
  relay_tokens_revoked?: number;
  // Added in v0.15.x: true when the user's sub was added to the revocation
  // registry so in-flight Okta JWTs are rejected on the hot request path.
  sub_marked_revoked?: boolean;
}

export interface ForceLogoutInput {
  email?: string;
  sub?: string;
}

// Cedar policy engine types -------------------------------------------------

export type CedarPolicyCategory =
  | 'resource_access'
  | 'tool_access';

export type CedarPolicyEffect = 'permit' | 'forbid';

export interface CedarPolicy {
  id: string;
  policy_id: string;
  policy_text: string;
  description?: string | null;
  category: CedarPolicyCategory;
  effect: CedarPolicyEffect;
  enabled: boolean;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  version: number;
  resource_name?: string | null;
  // NL provenance (present when policy was AI-authored)
  nl_source?: string | null;
  nl_generation_id?: string | null;
  llm_provider?: string | null;
  llm_model?: string | null;
  human_reviewed_at?: string | null;
  human_reviewed_by?: string | null;
}

// NL authoring types (P07/P11)
export interface NLHealthResponse {
  available: boolean;
  provider: string;
  model: string;
  circuit_state: string;
  rate_limit_remaining: number;
}

export interface NLToolMatched {
  name: string;
  annotation_source?: string;
  annotation_confidence?: string;
  reason_included?: string;
}

export interface NLCoverageConcern {
  tool: string;
  concern: string;
}

export interface NLExpectedTestCase {
  description?: string;
  principal: string;
  action: string;
  resource: string;
  context?: Record<string, any>;
  expected_decision: 'Allow' | 'Deny';
}

export interface NLGenerateResponse {
  session_id: string;
  generation_id: string;
  policy_text: string;
  suggested_policy_id: string;
  suggested_category: string;
  suggested_description: string;
  explanation: string;
  tools_matched: NLToolMatched[];
  coverage_concerns: NLCoverageConcern[];
  caveats: string[];
  expected_test_cases: NLExpectedTestCase[];
  validation: { passed: boolean; errors: string[] };
  provider: string;
  model: string;
  attempts: number;
  target_resource?: string | null;
}

export interface NLSimulateResponse {
  decision: 'Allow' | 'Deny';
  determining_policies: string[];
  errors: string[];
}

export interface NLPrepareCreateResponse {
  confirmation_token: string;
  expires_in_seconds: number;
  session_id: string;
}

export interface CedarPolicyCreateInput {
  policy_id: string;
  policy_text: string;
  description?: string | null;
  enabled?: boolean;
}

export interface CedarPolicyUpdateInput {
  policy_text?: string;
  description?: string | null;
  enabled?: boolean;
}

export interface CedarSimulateRequestInput {
  principal: string;
  action: string;
  resource: string;
  context?: Record<string, any>;
}

export interface CedarSimulateResponse {
  decision: 'Allow' | 'Deny';
  determining_policies: string[];
  errors: string[];
}

export interface CedarValidateResponse {
  valid: boolean;
  errors: string[];
}

export interface CedarStatsRow {
  policy_id: string;
  date: string;
  eval_count: number;
  match_count: number;
  deny_count: number;
}

export interface CedarAnalytics {
  total_evals: number;
  total_denies: number;
  deny_rate: number;
  avg_per_day: number;
  days: number;
  [key: string]: any;
}

export interface CedarTemplate {
  template_id: string;
  name: string;
  description: string;
  category: CedarPolicyCategory;
  policy_text: string;
}

export interface CedarConflictEntry {
  policy_id: string;
  effect: string;
  category: string;
  resource_name: string | null;
  enabled: boolean;
}

export interface CedarConflictCheckResponse {
  conflicts: CedarConflictEntry[];
  message: string;
}

export interface CedarEntityGroup {
  id: string;
  group_type: string;
  group_id: string;
  description?: string | null;
  members: string[];
  created_at: string;
}

export interface CedarAgentEntity {
  agent_id: string;
  agent_name?: string;
  enabled?: boolean;
}

export interface CedarToolEntity {
  resource_name: string;
  tool_name: string;
  sensitivity?: ToolSensitivity;
  mutating?: boolean | null;
  category?: string | null;
  tags?: string[];
  risk_level?: string | null;
}

export interface CedarSession {
  session_key: string;
  user_id: string;
  agent_id: string;
  mcp_session_id?: string | null;
  started_at: number;
  tool_call_count: number;
  distinct_backends: string[];
  distinct_tools: string[];
  tools_called_sequence: string[];
  last_tool_called?: string | null;
  accumulated_values: Record<string, any>;
}

export class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    // Bootstrap calls that legitimately run before sign-in must opt out of the
    // global 401 -> signOut() handling below. Without this, a pre-login 401
    // triggers signOut() -> redirect to /login -> provider remount -> refetch,
    // which is an infinite redirect loop. Needed for version skew too: a newer
    // Admin UI may talk to an older gateway that still gates /config/public.
    { skipAuthRedirect = false }: { skipAuthRedirect?: boolean } = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    const accessToken = oktaAuth.getAccessToken();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: 'unknown_error',
        message: response.statusText
      }));

      // Handle session expiration - redirect to sign in
      if (response.status === 401) {
        // Pre-sign-in bootstrap calls must fail quietly. Redirecting here would
        // bounce back to /login and remount the caller, looping forever.
        if (skipAuthRedirect) {
          const err = new Error(error.message || 'Unauthorized') as Error & {
            status?: number;
            code?: string;
          };
          err.status = 401;
          err.code = 'unauthorized';
          throw err;
        }

        console.log('[ApiClient] Session expired (401), redirecting to sign in...');
        // Sign out and redirect to login page
        // Use setTimeout to allow any pending UI updates (like toasts) to show
        setTimeout(async () => {
          await oktaAuth.signOut();
        }, 500);

        // Throw error with 401 status so pages can show appropriate message
        const err = new Error('Your Okta session has expired. Redirecting to sign in...') as Error & {
          status?: number;
          code?: string;
        };
        err.status = 401;
        err.code = 'session_expired';
        throw err;
      }

      // Provide actionable error messages for common auth failures
      let message = error.message || `HTTP ${response.status}`;
      if (response.status === 403) {
        if (error.error === 'insufficient_scopes') {
          message = 'Your Okta session lacks AI Agent management permissions. Go to your Okta app → Okta API Scopes tab and grant okta.aiAgents.read and okta.aiAgents.manage, then sign out and back in.';
        } else if (error.error === 'okta_auth_required') {
          message = 'Agent import requires Okta sign-in. Please sign out and use "Sign in with Okta".';
        }
      }

      const err = new Error(message) as Error & {
        status?: number;
        code?: string;
        required_scopes?: string[];
      };
      err.status = response.status;
      err.code = error.error;
      err.required_scopes = error.required_scopes;
      throw err;
    }

    // Handle 204 No Content responses (e.g., DELETE operations)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Authentication (DEPRECATED - use Okta OIDC via signIn())
  async login(_username: string, _password: string): Promise<{ access_token: string; token_type: string; expires_in: number }> {
    throw new Error('Legacy login is disabled. Please sign in with Okta.');
  }

  // Agents
  async listAgents(): Promise<Agent[]> {
    const response = await this.request<{ agents: Agent[] }>('/api/admin/agents');
    return response.agents;
  }

  async getAgent(agentId: string): Promise<Agent> {
    return this.request<Agent>(`/api/admin/agents/${agentId}`);
  }

  async createAgent(agent: Partial<Agent>): Promise<Agent> {
    return this.request<Agent>('/api/admin/agents', {
      method: 'POST',
      body: JSON.stringify(agent),
    });
  }

  async updateAgent(agentId: string, agent: Partial<Agent>): Promise<Agent> {
    return this.request<Agent>(`/api/admin/agents/${agentId}`, {
      method: 'PUT',
      body: JSON.stringify(agent),
    });
  }

  async deleteAgent(agentId: string): Promise<void> {
    await this.request(`/api/admin/agents/${agentId}`, {
      method: 'DELETE',
    });
  }

  // Resources
  async listResources(): Promise<Resource[]> {
    const response = await this.request<{ resources: Resource[] }>('/api/admin/resources');
    return response.resources;
  }

  // Backward-compat alias
  async listResolvedResources(): Promise<Resource[]> {
    return this.listResources();
  }

  async getResource(name: string): Promise<Resource> {
    return this.request<Resource>(`/api/admin/resources/${name}`);
  }

  // Backward-compat alias
  async getResolvedResource(name: string): Promise<Resource> {
    return this.getResource(name);
  }

  async updateResource(name: string, data: Partial<Resource>): Promise<Resource> {
    return this.request<Resource>(`/api/admin/resources/${name}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteResource(name: string, opts?: { force?: boolean }): Promise<void> {
    const path = opts?.force
      ? `/api/admin/resources/${name}?force=true`
      : `/api/admin/resources/${name}`;
    await this.request(path, { method: 'DELETE' });
  }

  /** Standalone master-resource creation (post migration 004). */
  async createResource(input: CreateResourceInput): Promise<Resource> {
    return this.request<Resource>('/api/admin/resources', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /** Merge ``source`` into ``target`` atomically; returns the move counts. */
  async mergeResource(source: string, target: string): Promise<MergeResult> {
    return this.request<MergeResult>(
      `/api/admin/resources/${encodeURIComponent(source)}/merge`,
      {
        method: 'POST',
        body: JSON.stringify({ into: target }),
      },
    );
  }

  /** GET /api/admin/resources/merge-candidates — groups of masters that
   * share a connection_resource_id. Used by the Resources page
   * "Suggested merges" panel. */
  async listMergeCandidates(): Promise<MergeCandidateGroup[]> {
    const response = await this.request<{ candidates: MergeCandidateGroup[] }>(
      '/api/admin/resources/merge-candidates',
    );
    return response.candidates;
  }

  /** Link an (agent, connection) tuple to a master resource. */
  async linkResourceToConnection(
    agentId: string,
    connectionId: string,
    resourceName: string,
  ): Promise<void> {
    await this.request(
      `/api/admin/agents/${encodeURIComponent(agentId)}/connections/${encodeURIComponent(connectionId)}/resource`,
      {
        method: 'PUT',
        body: JSON.stringify({ resource_name: resourceName }),
      },
    );
  }

  /** Detach an (agent, connection) from its master resource. The
   * agent_connection row remains; its resource_id becomes NULL. */
  async unlinkResourceFromConnection(
    agentId: string,
    connectionId: string,
  ): Promise<void> {
    await this.request(
      `/api/admin/agents/${encodeURIComponent(agentId)}/connections/${encodeURIComponent(connectionId)}/resource`,
      { method: 'DELETE' },
    );
  }

  // Connection Sync
  async syncConnections(): Promise<SyncResult> {
    return this.request<SyncResult>('/api/admin/connections/sync', {
      method: 'POST',
    });
  }

  async getConnectionStatus(): Promise<ConnectionStatus> {
    return this.request<ConnectionStatus>('/api/admin/connections/status');
  }

  // Audit Log
  async getAuditLog(params?: {
    entity_type?: string;
    entity_id?: string;
    limit?: number;
  }): Promise<AuditLogEntry[]> {
    const queryParams = new URLSearchParams();
    if (params?.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params?.entity_id) queryParams.append('entity_id', params.entity_id);
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const query = queryParams.toString();
    const endpoint = query ? `/api/admin/audit?${query}` : '/api/admin/audit';

    const response = await this.request<{ audit_log: AuditLogEntry[] }>(endpoint);
    return response.audit_log;
  }

  async getAgentAuditLog(agentId: string): Promise<AuditLogEntry[]> {
    const response = await this.request<{ audit_log: AuditLogEntry[] }>(
      `/api/admin/agents/${agentId}/audit`
    );
    return response.audit_log;
  }

  async getResourceAuditLog(name: string): Promise<AuditLogEntry[]> {
    const response = await this.request<{ audit_log: AuditLogEntry[] }>(
      `/api/admin/resources/${name}/audit`
    );
    return response.audit_log;
  }

  // Cloud Log Viewer
  async getEventLogs(params?: {
    start_minutes_ago?: number;
    limit?: number;
    event_type?: string;
    agent_id?: string;
    severity?: string;
    correlation_id?: string;
  }): Promise<EventLogResponse> {
    const queryParams = new URLSearchParams();
    if (params?.start_minutes_ago) queryParams.append('start_minutes_ago', params.start_minutes_ago.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.event_type) queryParams.append('event_type', params.event_type);
    if (params?.agent_id) queryParams.append('agent_id', params.agent_id);
    if (params?.severity) queryParams.append('severity', params.severity);
    if (params?.correlation_id) queryParams.append('correlation_id', params.correlation_id);
    const query = queryParams.toString();
    const endpoint = query ? `/api/admin/events/logs?${query}` : '/api/admin/events/logs';
    return this.request<EventLogResponse>(endpoint);
  }

  // Shared by export* methods below — CSV export endpoints return a file,
  // not JSON, so they bypass request<T>() but reuse its auth/401 handling.
  private async fetchBlob(endpoint: string): Promise<Blob> {
    const token = oktaAuth.getAccessToken() || null;
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, { headers });

    if (!response.ok) {
      if (response.status === 401) {
        console.log('[ApiClient] Session expired (401), redirecting to sign in...');
        setTimeout(async () => {
          await oktaAuth.signOut();
        }, 500);
        const err = new Error('Your Okta session has expired. Redirecting to sign in...') as Error & {
          status?: number;
          code?: string;
        };
        err.status = 401;
        err.code = 'session_expired';
        throw err;
      }

      const error = await response.json().catch(() => ({
        error: 'unknown_error',
        message: response.statusText,
      }));
      let message = error.message || `HTTP ${response.status}`;
      if (response.status === 403 && error.error === 'insufficient_scopes') {
        message = 'Your Okta session lacks AI Agent management permissions. Go to your Okta app → Okta API Scopes tab and grant okta.aiAgents.read and okta.aiAgents.manage, then sign out and back in.';
      }
      const err = new Error(message) as Error & { status?: number; code?: string };
      err.status = response.status;
      err.code = error.error;
      throw err;
    }

    return response.blob();
  }

  async exportAuditLog(params?: {
    entity_type?: string;
    entity_id?: string;
    limit?: number;
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params?.entity_id) queryParams.append('entity_id', params.entity_id);
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const query = queryParams.toString();
    const endpoint = query ? `/api/admin/audit/export?${query}` : '/api/admin/audit/export';
    return this.fetchBlob(endpoint);
  }

  async exportEventLogs(params?: {
    start_minutes_ago?: number;
    limit?: number;
    event_type?: string;
    agent_id?: string;
    severity?: string;
    correlation_id?: string;
    event_type_prefixes?: string;
    outcome?: string;
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.start_minutes_ago) queryParams.append('start_minutes_ago', params.start_minutes_ago.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.event_type) queryParams.append('event_type', params.event_type);
    if (params?.agent_id) queryParams.append('agent_id', params.agent_id);
    if (params?.severity) queryParams.append('severity', params.severity);
    if (params?.correlation_id) queryParams.append('correlation_id', params.correlation_id);
    if (params?.event_type_prefixes) queryParams.append('event_type_prefixes', params.event_type_prefixes);
    if (params?.outcome) queryParams.append('outcome', params.outcome);
    const query = queryParams.toString();
    const endpoint = query ? `/api/admin/events/logs/export?${query}` : '/api/admin/events/logs/export';
    return this.fetchBlob(endpoint);
  }

  // Okta AI Agent Import
  async listOktaAgents(): Promise<OktaAgent[]> {
    const response = await this.request<{ agents: OktaAgent[] }>('/api/admin/okta/agents');
    return response.agents;
  }

  async getOktaAgentDetail(oktaAgentId: string): Promise<OktaAgentDetail> {
    return this.request<OktaAgentDetail>(`/api/admin/okta/agents/${oktaAgentId}`);
  }

  async importOktaAgent(
    oktaAgentId: string,
    options?: {
      resource_mapping?: Record<string, string>;
      agent_name?: string;
      selected_delegation_app_id?: string;
    }
  ): Promise<ImportResult> {
    return this.request<ImportResult>(`/api/admin/okta/agents/${oktaAgentId}/import`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
  }

  async listDelegationApps(oktaAgentId: string): Promise<DelegationAppCandidate[]> {
    const response = await this.request<{ candidates: DelegationAppCandidate[] }>(
      `/api/admin/okta/agents/${oktaAgentId}/delegation-apps`
    );
    return response.candidates;
  }

  async bulkImportOktaAgents(agentIds: string[]): Promise<{
    results: ImportResult[];
    imported: number;
    failed: number;
  }> {
    return this.request(`/api/admin/okta/agents/bulk-import`, {
      method: 'POST',
      body: JSON.stringify({ agent_ids: agentIds }),
    });
  }

  async syncOktaAgent(oktaAgentId: string): Promise<ImportResult> {
    return this.request<ImportResult>(`/api/admin/okta/agents/${oktaAgentId}/sync`, {
      method: 'POST',
    });
  }

  async syncAllAgents(): Promise<{
    results: ImportResult[];
    synced: number;
    failed: number;
    unchanged: number;
    connection_sync?: {
      resources_resolved: number;
      unresolved: number;
    };
  }> {
    return this.request(`/api/admin/okta/sync/agents`, { method: 'POST' });
  }

  async syncAllResources(): Promise<{
    resources_updated: string[];
    unchanged: string[];
  }> {
    return this.request(`/api/admin/okta/sync/resources`, { method: 'POST' });
  }

  async syncAll(): Promise<SyncAllResult> {
    return this.request<SyncAllResult>(`/api/admin/okta/sync/all`, { method: 'POST' });
  }

  // Agent Connections & Resource Linkage

  async getAgentConnections(agentId: string): Promise<AgentConnectionsResponse> {
    return this.request<AgentConnectionsResponse>(`/api/admin/agents/${agentId}/connections`);
  }

  async getAgentDetail(agentId: string): Promise<AgentDetail> {
    return this.request<AgentDetail>(`/api/admin/agents/${agentId}/connections`);
  }

  async getAgentTools(
    agentId: string,
    options?: { includeDisabled?: boolean; limit?: number; offset?: number },
  ): Promise<AgentToolsResponse> {
    const q = new URLSearchParams();
    if (options?.includeDisabled) q.append('include_disabled', 'true');
    if (options?.limit !== undefined) q.append('limit', String(options.limit));
    if (options?.offset !== undefined) q.append('offset', String(options.offset));
    const qs = q.toString();
    return this.request<AgentToolsResponse>(
      `/api/admin/agents/${agentId}/tools${qs ? `?${qs}` : ''}`,
    );
  }

  // Backward-compat alias for the system-wide call.
  async listAgentTools(
    agentId: string,
    options?: { includeDisabled?: boolean; limit?: number; offset?: number },
  ): Promise<AgentToolsResponse> {
    return this.getAgentTools(agentId, options);
  }

  // Tool inventory ---------------------------------------------------------

  async listAllTools(params?: {
    resource_name?: string;
    sensitivity?: 'low' | 'medium' | 'high' | 'critical' | 'unclassified';
    enabled?: 'true' | 'false' | 'all';
    q?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListAllToolsResponse> {
    const q = new URLSearchParams();
    if (params?.resource_name) q.append('resource_name', params.resource_name);
    if (params?.sensitivity) q.append('sensitivity', params.sensitivity);
    if (params?.enabled) q.append('enabled', params.enabled);
    if (params?.q) q.append('q', params.q);
    if (params?.limit !== undefined) q.append('limit', String(params.limit));
    if (params?.offset !== undefined) q.append('offset', String(params.offset));
    const query = q.toString();
    const endpoint = query ? `/api/admin/tools?${query}` : '/api/admin/tools';
    return this.request<ListAllToolsResponse>(endpoint);
  }

  async listResourceTools(
    resourceName: string,
    params?: { q?: string; limit?: number; offset?: number },
  ): Promise<ListResourceToolsResponse> {
    const q = new URLSearchParams();
    if (params?.q) q.append('q', params.q);
    if (params?.limit !== undefined) q.append('limit', String(params.limit));
    if (params?.offset !== undefined) q.append('offset', String(params.offset));
    const query = q.toString();
    return this.request<ListResourceToolsResponse>(
      `/api/admin/resources/${encodeURIComponent(resourceName)}/tools${query ? `?${query}` : ''}`,
    );
  }

  async listResourceToolCounts(): Promise<ListResourceToolCountsResponse> {
    return this.request<ListResourceToolCountsResponse>(
      '/api/admin/resources/tool-counts',
    );
  }

  async updateToolClassification(
    resourceName: string,
    toolName: string,
    data: ToolClassificationInput,
  ): Promise<ResourceTool> {
    return this.request<ResourceTool>(
      `/api/admin/resources/${encodeURIComponent(resourceName)}/tools/${encodeURIComponent(toolName)}`,
      { method: 'PUT', body: JSON.stringify(data) },
    );
  }

  async bulkClassifyTools(
    resourceName: string,
    tools: BulkClassifyEntry[],
  ): Promise<BulkClassifyResult> {
    return this.request<BulkClassifyResult>(
      `/api/admin/resources/${encodeURIComponent(resourceName)}/tools/bulk-classify`,
      { method: 'PUT', body: JSON.stringify({ tools }) },
    );
  }

  async enableTool(
    resourceName: string,
    toolName: string,
    reason?: string,
  ): Promise<ResourceTool> {
    return this.request<ResourceTool>(
      `/api/admin/resources/${encodeURIComponent(resourceName)}/tools/${encodeURIComponent(toolName)}/enable`,
      {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      },
    );
  }

  async disableTool(
    resourceName: string,
    toolName: string,
    reason: string,
  ): Promise<ResourceTool> {
    return this.request<ResourceTool>(
      `/api/admin/resources/${encodeURIComponent(resourceName)}/tools/${encodeURIComponent(toolName)}/disable`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    );
  }

  async triggerResourceDiscovery(resourceName: string): Promise<DiscoveryTriggerResult> {
    return this.request<DiscoveryTriggerResult>(
      `/api/admin/resources/${encodeURIComponent(resourceName)}/tools/discover`,
      { method: 'POST' },
    );
  }

  async triggerFullDiscovery(): Promise<DiscoveryTriggerResult> {
    return this.request<DiscoveryTriggerResult>('/api/admin/tools/discover', {
      method: 'POST',
    });
  }

  async listDiscoveryState(
    params?: { limit?: number; offset?: number },
  ): Promise<DiscoveryStateListResponse> {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.append('limit', String(params.limit));
    if (params?.offset !== undefined) q.append('offset', String(params.offset));
    const query = q.toString();
    return this.request<DiscoveryStateListResponse>(
      `/api/admin/tools/discovery-state${query ? `?${query}` : ''}`,
    );
  }

  // Gateway Config (public metadata for user-facing URLs)

  async getPublicConfig(): Promise<PublicConfig> {
    // Fetched by the global FeatureFlagsProvider on every route, including
    // /login, so it must never trigger the 401 -> signOut() redirect path.
    return this.request<PublicConfig>(
      '/api/admin/config/public',
      {},
      { skipAuthRedirect: true },
    );
  }

  // Credential Automation

  async getCredentialStatus(agentId: string): Promise<CredentialStatus> {
    return this.request<CredentialStatus>(`/api/admin/agents/${agentId}/credentials/status`);
  }

  async fetchClientSecret(agentId: string): Promise<FetchSecretResult> {
    return this.request<FetchSecretResult>(`/api/admin/agents/${agentId}/credentials/fetch-secret`, {
      method: 'POST',
    });
  }

  async generateKeyPair(
    agentId: string,
    deliveryMode: 'push' | 'jwks_uri' = 'jwks_uri',
  ): Promise<GenerateKeyPairResult> {
    return this.request<GenerateKeyPairResult>(`/api/admin/agents/${agentId}/credentials/generate-keypair`, {
      method: 'POST',
      body: JSON.stringify({ delivery_mode: deliveryMode }),
    });
  }

  // Client Secret Proxy (compatibility shim for clients without DCR/CIMD support)

  async getProxySecretStatus(agentId: string): Promise<ProxySecretStatus> {
    return this.request<ProxySecretStatus>(`/api/admin/agents/${agentId}/proxy-secret/status`);
  }

  async issueProxySecret(agentId: string): Promise<ProxySecretReveal> {
    return this.request<ProxySecretReveal>(`/api/admin/agents/${agentId}/proxy-secret`, {
      method: 'POST',
    });
  }

  async rotateProxySecret(agentId: string): Promise<ProxySecretReveal> {
    return this.request<ProxySecretReveal>(`/api/admin/agents/${agentId}/proxy-secret/rotate`, {
      method: 'POST',
    });
  }

  async revealProxySecret(agentId: string): Promise<ProxySecretReveal> {
    return this.request<ProxySecretReveal>(`/api/admin/agents/${agentId}/proxy-secret/reveal`, {
      method: 'POST',
    });
  }

  async revokeProxySecret(agentId: string): Promise<void> {
    return this.request<void>(`/api/admin/agents/${agentId}/proxy-secret`, {
      method: 'DELETE',
    });
  }

  // Admin-initiated force logout
  async forceLogoutUser(input: ForceLogoutInput): Promise<ForceLogoutSummary> {
    return this.request<ForceLogoutSummary>('/api/admin/users/force-logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  // Cedar policy engine -----------------------------------------------------

  async listCedarPolicies(
    params?: { enabled?: boolean; category?: CedarPolicyCategory; resource_name?: string },
  ): Promise<CedarPolicy[]> {
    const q = new URLSearchParams();
    if (params?.enabled !== undefined) q.append('enabled', String(params.enabled));
    if (params?.category) q.append('category', params.category);
    if (params?.resource_name) q.append('resource_name', params.resource_name);
    const qs = q.toString();
    const resp = await this.request<{ policies: CedarPolicy[] }>(
      `/api/admin/cedar/policies${qs ? `?${qs}` : ''}`,
    );
    return resp.policies;
  }

  async getCedarPolicy(policyId: string): Promise<CedarPolicy> {
    return this.request<CedarPolicy>(
      `/api/admin/cedar/policies/${encodeURIComponent(policyId)}`,
    );
  }

  async createCedarPolicy(data: CedarPolicyCreateInput): Promise<CedarPolicy> {
    return this.request<CedarPolicy>('/api/admin/cedar/policies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCedarPolicy(
    policyId: string,
    data: CedarPolicyUpdateInput,
  ): Promise<CedarPolicy> {
    return this.request<CedarPolicy>(
      `/api/admin/cedar/policies/${encodeURIComponent(policyId)}`,
      { method: 'PUT', body: JSON.stringify(data) },
    );
  }

  async deleteCedarPolicy(policyId: string): Promise<void> {
    await this.request(
      `/api/admin/cedar/policies/${encodeURIComponent(policyId)}`,
      { method: 'DELETE' },
    );
  }

  async enableCedarPolicy(policyId: string): Promise<CedarPolicy> {
    return this.request<CedarPolicy>(
      `/api/admin/cedar/policies/${encodeURIComponent(policyId)}/enable`,
      { method: 'PUT' },
    );
  }

  async disableCedarPolicy(policyId: string): Promise<CedarPolicy> {
    return this.request<CedarPolicy>(
      `/api/admin/cedar/policies/${encodeURIComponent(policyId)}/disable`,
      { method: 'PUT' },
    );
  }

  async validateCedarPolicy(policyText: string): Promise<CedarValidateResponse> {
    return this.request<CedarValidateResponse>(
      '/api/admin/cedar/policies/validate',
      { method: 'PUT', body: JSON.stringify({ policy_text: policyText }) },
    );
  }

  async simulateCedar(req: CedarSimulateRequestInput): Promise<CedarSimulateResponse> {
    return this.request<CedarSimulateResponse>('/api/admin/cedar/simulate', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  }

  async simulateCedarBatch(
    requests: CedarSimulateRequestInput[],
  ): Promise<{ results: CedarSimulateResponse[] }> {
    return this.request<{ results: CedarSimulateResponse[] }>(
      '/api/admin/cedar/simulate/batch',
      { method: 'POST', body: JSON.stringify({ requests }) },
    );
  }

  async getCedarSchema(): Promise<{ schema: any }> {
    return this.request<{ schema: any }>('/api/admin/cedar/schema');
  }

  async listCedarAgentEntities(): Promise<CedarAgentEntity[]> {
    const resp = await this.request<{ agents: CedarAgentEntity[] }>(
      '/api/admin/cedar/entities/agents',
    );
    return resp.agents;
  }

  async listCedarToolEntities(backend?: string): Promise<CedarToolEntity[]> {
    const q = new URLSearchParams();
    if (backend) q.append('backend', backend);
    const qs = q.toString();
    const resp = await this.request<{ tools: CedarToolEntity[] }>(
      `/api/admin/cedar/entities/tools${qs ? `?${qs}` : ''}`,
    );
    return resp.tools;
  }

  async listCedarEntityGroups(): Promise<CedarEntityGroup[]> {
    const resp = await this.request<{ groups: CedarEntityGroup[] }>(
      '/api/admin/cedar/entities/groups',
    );
    return resp.groups;
  }

  async createCedarEntityGroup(
    data: { group_type: string; group_id: string; description?: string; members?: string[] },
  ): Promise<CedarEntityGroup> {
    return this.request<CedarEntityGroup>('/api/admin/cedar/entities/groups', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCedarEntityGroup(
    groupId: string,
    data: { description?: string; members?: string[] },
  ): Promise<CedarEntityGroup> {
    return this.request<CedarEntityGroup>(
      `/api/admin/cedar/entities/groups/${encodeURIComponent(groupId)}`,
      { method: 'PUT', body: JSON.stringify(data) },
    );
  }

  async deleteCedarEntityGroup(groupId: string): Promise<void> {
    await this.request(
      `/api/admin/cedar/entities/groups/${encodeURIComponent(groupId)}`,
      { method: 'DELETE' },
    );
  }

  async getCedarTemplates(): Promise<CedarTemplate[]> {
    const resp = await this.request<{ templates: CedarTemplate[] }>(
      '/api/admin/cedar/templates',
    );
    return resp.templates;
  }

  async checkPoliciesConflict(body: {
    policy_text: string;
    resource_name?: string | null;
  }): Promise<CedarConflictCheckResponse> {
    return this.request<CedarConflictCheckResponse>(
      '/api/admin/cedar/policies/check-conflicts',
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  async listCedarSessions(limit?: number): Promise<CedarSession[]> {
    const q = new URLSearchParams();
    if (limit !== undefined) q.append('limit', String(limit));
    const qs = q.toString();
    const resp = await this.request<{ sessions: CedarSession[] }>(
      `/api/admin/cedar/sessions${qs ? `?${qs}` : ''}`,
    );
    return resp.sessions;
  }

  async getCedarSession(sessionKey: string): Promise<CedarSession> {
    return this.request<CedarSession>(
      `/api/admin/cedar/sessions/${encodeURIComponent(sessionKey)}`,
    );
  }

  async getCedarSessionTimeline(sessionKey: string): Promise<{ timeline: any[] }> {
    return this.request<{ timeline: any[] }>(
      `/api/admin/cedar/sessions/${encodeURIComponent(sessionKey)}/timeline`,
    );
  }

  async getCedarPolicyStats(policyId: string, days = 30): Promise<CedarStatsRow[]> {
    const resp = await this.request<{ stats: CedarStatsRow[] }>(
      `/api/admin/cedar/policies/${encodeURIComponent(policyId)}/stats?days=${days}`,
    );
    return resp.stats;
  }

  async getCedarAnalytics(days = 7): Promise<CedarAnalytics> {
    return this.request<CedarAnalytics>(
      `/api/admin/cedar/analytics?days=${days}`,
    );
  }

  // Tool annotations (P10) --------------------------------------------------

  async getToolAnnotations(resourceName: string, toolName: string): Promise<ToolAnnotations> {
    return this.request<ToolAnnotations>(
      `/api/admin/resources/${encodeURIComponent(resourceName)}/tools/${encodeURIComponent(toolName)}/annotations`,
    );
  }

  async setToolAnnotations(
    resourceName: string,
    toolName: string,
    data: SetAnnotationsInput,
  ): Promise<ToolAnnotations> {
    return this.request<ToolAnnotations>(
      `/api/admin/resources/${encodeURIComponent(resourceName)}/tools/${encodeURIComponent(toolName)}/annotations`,
      { method: 'PUT', body: JSON.stringify(data) },
    );
  }

  async deleteToolAnnotations(resourceName: string, toolName: string): Promise<void> {
    await this.request(
      `/api/admin/resources/${encodeURIComponent(resourceName)}/tools/${encodeURIComponent(toolName)}/annotations`,
      { method: 'DELETE' },
    );
  }

  // NL authoring endpoints (P07/P11) ----------------------------------------

  async nlHealth(): Promise<NLHealthResponse> {
    return this.request<NLHealthResponse>('/api/admin/cedar/nl/health', { method: 'POST' });
  }

  async nlGenerate(body: {
    natural_language: string;
    target_resource?: string | null;
    session_id?: string | null;
    intent_hint?: string | null;
  }): Promise<NLGenerateResponse> {
    return this.request<NLGenerateResponse>('/api/admin/cedar/nl/generate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async nlRefine(body: {
    session_id: string;
    refinement: string;
  }): Promise<NLGenerateResponse> {
    return this.request<NLGenerateResponse>('/api/admin/cedar/nl/refine', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async nlSimulate(body: {
    session_id: string;
    policy_text: string;
    principal: string;
    action: string;
    resource: string;
    context?: Record<string, any>;
  }): Promise<NLSimulateResponse> {
    return this.request<NLSimulateResponse>('/api/admin/cedar/nl/simulate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async nlPrepareCreate(body: {
    session_id: string;
    policy_text: string;
    policy_id: string;
    description?: string | null;
    enabled: boolean;
  }): Promise<NLPrepareCreateResponse> {
    return this.request<NLPrepareCreateResponse>('/api/admin/cedar/nl/prepare_create', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async nlCommitCreate(body: {
    session_id: string;
    confirmation_token: string;
  }): Promise<CedarPolicy> {
    return this.request<CedarPolicy>('/api/admin/cedar/nl/commit_create', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async nlGetGeneration(generationId: string): Promise<Record<string, any>> {
    return this.request<Record<string, any>>(
      `/api/admin/cedar/nl/generations/${encodeURIComponent(generationId)}`,
    );
  }
}

export const apiClient = new ApiClient();
